import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  AlertTriangle, CheckCircle, Shield, Clock, MapPin, 
  FileText, Eye, Download, ArrowLeft, ExternalLink, Info
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SharedScanPage = () => {
  const { shareToken } = useParams();
  const [scan, setScan] = useState(null);
  const [shareInfo, setShareInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSharedScan = async () => {
      try {
        const response = await fetch(`${API}/api/ai-hazard-scan/shared/${shareToken}`);
        
        if (!response.ok) {
          if (response.status === 404) {
            throw new Error('This share link is invalid or has been removed.');
          } else if (response.status === 410) {
            throw new Error('This share link has expired.');
          }
          throw new Error('Failed to load shared scan');
        }
        
        const data = await response.json();
        
        if (data.success) {
          setScan(data.scan);
          setShareInfo(data.share_info);
        } else {
          throw new Error('Failed to load scan data');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (shareToken) {
      fetchSharedScan();
    }
  }, [shareToken]);

  const getRiskBadge = (risk) => {
    const riskLower = (risk || 'unknown').toLowerCase();
    const colors = {
      low: 'bg-green-100 text-green-800 border-green-200',
      medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      high: 'bg-orange-100 text-orange-800 border-orange-200',
      critical: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[riskLower] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getSeverityColor = (severity) => {
    const severityLower = (severity || 'unknown').toLowerCase();
    const colors = {
      low: 'text-green-600 bg-green-50',
      medium: 'text-yellow-600 bg-yellow-50',
      high: 'text-orange-600 bg-orange-50',
      critical: 'text-red-600 bg-red-50'
    };
    return colors[severityLower] || 'text-gray-600 bg-gray-50';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading shared report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Unable to Load Report</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <RouterLink to="/">
              <Button>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Go to Homepage
              </Button>
            </RouterLink>
          </CardContent>
        </Card>
      </div>
    );
  }

  const analysis = scan?.analysis || {};
  const hazards = analysis.hazards_found || [];
  const overallRisk = scan?.overall_risk || analysis.overall_risk_level || 'Unknown';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-lg text-gray-900">WSafe AI</h1>
              <p className="text-xs text-gray-500">Shared Hazard Scan Report</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Eye className="w-4 h-4" />
            <span>{shareInfo?.view_count || 0} views</span>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Scan Overview */}
        <Card className="mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-bold mb-1">{scan?.scan_number || 'Hazard Scan Report'}</h2>
                <p className="text-blue-100 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {scan?.location || 'Unknown location'}
                  {scan?.project_name && ` • ${scan.project_name}`}
                </p>
              </div>
              <Badge className={`${getRiskBadge(overallRisk)} text-sm px-3 py-1`}>
                {overallRisk.toUpperCase()} RISK
              </Badge>
            </div>
            <div className="mt-4 flex items-center gap-4 text-sm text-blue-100">
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {scan?.created_at ? new Date(scan.created_at).toLocaleString() : 'N/A'}
              </span>
              <span className="flex items-center gap-1">
                <FileText className="w-4 h-4" />
                {scan?.scan_type || 'General'} Scan
              </span>
            </div>
          </div>
        </Card>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Scanned Image */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Eye className="w-5 h-5 text-blue-500" />
                Scanned Image
              </CardTitle>
            </CardHeader>
            <CardContent>
              {scan?.image_url ? (
                <img 
                  src={scan.image_url} 
                  alt="Scanned site" 
                  className="w-full rounded-lg border"
                />
              ) : (
                <div className="bg-gray-100 rounded-lg h-64 flex items-center justify-center text-gray-400">
                  No image available
                </div>
              )}
            </CardContent>
          </Card>

          {/* Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Info className="w-5 h-5 text-blue-500" />
                Analysis Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-700">{analysis.summary || 'No summary available.'}</p>
              
              {analysis.positive_observations?.length > 0 && (
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <h4 className="font-medium text-green-800 mb-2 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Positive Observations
                  </h4>
                  <ul className="space-y-1">
                    {analysis.positive_observations.map((obs, i) => (
                      <li key={i} className="text-green-700 text-sm flex items-start gap-2">
                        <span className="text-green-500 mt-1">✓</span>
                        {obs}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {analysis.immediate_actions_required?.length > 0 && (
                <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                  <h4 className="font-medium text-orange-800 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Immediate Actions Required
                  </h4>
                  <ul className="space-y-1">
                    {analysis.immediate_actions_required.map((action, i) => (
                      <li key={i} className="text-orange-700 text-sm flex items-start gap-2">
                        <span className="text-orange-500 mt-1">•</span>
                        {action}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Hazards Found */}
        {hazards.length > 0 && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                Hazards Detected ({hazards.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {hazards.map((hazard, index) => (
                  <div 
                    key={index} 
                    className="border rounded-lg p-4 hover:shadow-sm transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-semibold text-gray-900">{hazard.hazard_type || 'Unknown Hazard'}</h4>
                      <Badge className={`${getSeverityColor(hazard.severity)} border`}>
                        {(hazard.severity || 'unknown').toUpperCase()}
                      </Badge>
                    </div>
                    <p className="text-gray-600 text-sm mb-3">{hazard.description || 'No description'}</p>
                    
                    <div className="grid sm:grid-cols-2 gap-3 text-sm">
                      {hazard.location_in_image && (
                        <div className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-600">{hazard.location_in_image}</span>
                        </div>
                      )}
                      {hazard.recommended_action && (
                        <div className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-600">{hazard.recommended_action}</span>
                        </div>
                      )}
                      {hazard.osha_reference && (
                        <div className="flex items-start gap-2 sm:col-span-2">
                          <FileText className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                          <span className="text-blue-600">OSHA: {hazard.osha_reference}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>This report was generated by WSafe AI - AI-Powered Safety Management</p>
          {shareInfo?.expires_at && (
            <p className="mt-1">
              Link expires: {new Date(shareInfo.expires_at).toLocaleDateString()}
            </p>
          )}
        </div>
      </main>
    </div>
  );
};

export default SharedScanPage;
